// Rewrites Supabase Storage public URLs to the on-the-fly image-render
// endpoint so we can ship smaller, format-negotiated thumbnails to the
// browser instead of full-resolution originals.
//
// Public URL : .../storage/v1/object/public/<bucket>/<path>
// Render URL : .../storage/v1/render/image/public/<bucket>/<path>?width=...
//
// For non-Supabase URLs (or anything we can't recognise) we return the
// original URL untouched.

export type ImgTransform = {
  width?: number;
  height?: number;
  quality?: number; // 20–100
  resize?: "cover" | "contain" | "fill";
};

export function transformedImageUrl(url: string | null | undefined, opts: ImgTransform = {}): string {
  if (!url) return "";
  if (!url.includes("/storage/v1/object/public/")) return url;
  const rendered = url.replace("/storage/v1/object/public/", "/storage/v1/render/image/public/");
  const params = new URLSearchParams();
  if (opts.width) params.set("width", String(Math.round(opts.width)));
  if (opts.height) params.set("height", String(Math.round(opts.height)));
  params.set("quality", String(opts.quality ?? 70));
  params.set("resize", opts.resize ?? "cover");
  return `${rendered}?${params.toString()}`;
}

/**
 * Build a `srcset` string for responsive `<img>` tags. The browser picks
 * the best variant based on the rendered size and DPR.
 */
export function imageSrcSet(url: string | null | undefined, widths: number[], opts: Omit<ImgTransform, "width"> = {}): string {
  if (!url) return "";
  return widths
    .map((w) => `${transformedImageUrl(url, { ...opts, width: w })} ${w}w`)
    .join(", ");
}

/**
 * Produce a stable comparison key for an image URL so two references to the
 * same underlying file match even when their URL strings differ (render vs
 * object endpoint, transform query params, percent-encoding, trailing
 * slashes, http/https). Falls back to the storage object path or the file
 * basename so cross-list matching is resilient.
 */
export function imageUrlKey(url: string | null | undefined): string {
  if (!url) return "";
  let u = url.trim();
  // Normalise the Supabase render endpoint back to the canonical object path.
  u = u.replace("/storage/v1/render/image/public/", "/storage/v1/object/public/");
  // Drop the query string (transform params like width/quality/resize).
  const qIdx = u.indexOf("?");
  if (qIdx !== -1) u = u.slice(0, qIdx);
  // Prefer the storage object path (bucket + path) when present.
  const marker = "/storage/v1/object/public/";
  const mIdx = u.indexOf(marker);
  if (mIdx !== -1) u = u.slice(mIdx + marker.length);
  // Decode percent-encoding and strip trailing slashes.
  try {
    u = decodeURIComponent(u);
  } catch {
    /* keep raw value if it is not valid percent-encoding */
  }
  u = u.replace(/\/+$/, "");
  return u.toLowerCase();
}

/** True when two image URLs point at the same underlying file. */
export function sameImageUrl(a: string | null | undefined, b: string | null | undefined): boolean {
  const ka = imageUrlKey(a);
  const kb = imageUrlKey(b);
  if (!ka || !kb) return false;
  if (ka === kb) return true;
  // Last-resort fallback: compare by file basename.
  const ba = ka.split("/").pop() ?? ka;
  const bb = kb.split("/").pop() ?? kb;
  return ba === bb;
}